import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { format } from 'date-fns';
import { useFormik } from 'formik';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useExerciseResultsForSetAutofill } from '../../../hooks/use-exercise-results-for-set-autofill';
import {
  isActivityTypeSubmitBlocked,
  resolveActivityTypeIdForSubmit,
  useTrainingActivityTypePicker,
  useTrainingActivityTypePreselect,
} from '../../../hooks/use-training-activity-type-picker';
import { useProgramItemExercises } from '../../../hooks/useProgramItemExercises';
import {
  useGetAvailableUserProgramItems,
  useGetProgramItems,
  useLazyGetProgramItemById,
} from '../../../redux/api/programsAPI';
import { useStartNewTraining } from '../../../redux/api/trainingsAPI';
import { TrainingSetItemStatus } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { ITraining } from '../../../types/interfaces/training';
import { groupBy, orderBy } from '../../../utils/collections/array-utils';
import { generateTemporaryId } from '../../../utils/training/set-utils';
import { useEditTraining } from '../edit-training/useEditTraining';

export function useStartNewTrainingPage() {
  const { t } = useTranslation();
  const { replace } = useNavigationStack();
  const { data: programItems } = useGetProgramItems();
  const { data: userAvailableProgramItems } = useGetAvailableUserProgramItems();
  const [getProgramItem, { data: programItem }] = useLazyGetProgramItemById();
  const [startNewTraining, { isLoading }] = useStartNewTraining();
  const { exercises } = useProgramItemExercises({
    programItemId: programItem?.id,
  });
  const defaultTrainingName = format(new Date(), 'dd.MM.yyyy');
  const [showSavedTrainingModal, setShowSavedTrainingModal] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: {
      title: '',
      programItem: null,
      trainingSets: [],
      activityTypeId: null,
    },
    onSubmit,
  });

  useTrainingActivityTypePreselect(formik);

  const {
    activityTypes,
    isLoading: isActivityTypesLoading,
    isError: isActivityTypesError,
    defaultActivityTypeId,
  } = useTrainingActivityTypePicker();

  const isSubmitDisabled = isActivityTypeSubmitBlocked(
    isActivityTypesLoading,
    activityTypes,
    formik.values.activityTypeId,
    defaultActivityTypeId,
    isActivityTypesError,
  );

  useEffect(() => {
    if (programItem && exercises) {
      const trainingSets = getTrainingSetsFromProgramSets(
        programItem.programSets,
      );

      formik.setFieldValue('trainingSets', trainingSets);
    }
  }, [programItem, exercises]);

  function onSubmit(values: {
    title: string;
    programItem?: { id: string } | null;
    activityTypeId?: string | null;
    trainingSets: ITraining['trainingSets'];
  }) {
    const body = {
      title: values.title || defaultTrainingName,
      programItemId: values.programItem?.id,
      activityTypeId: resolveActivityTypeIdForSubmit(
        values.activityTypeId,
        defaultActivityTypeId,
        activityTypes,
      ),
      trainingSets: values.trainingSets.map((set) => {
        const { exercise, ...other } = set;
        return other;
      }),
    };

    startNewTraining({ body })
      .unwrap()
      .then((response) => {
        if (response?.id) {
          replace(Paths.trainings.toCurrent(response.id));
        } else {
          replace(Paths.trainings.toList);
        }
      })
      .catch((error) => console.log(error));
  }

  function getTrainingSetsFromProgramSets(programSets: IProgramSet[]) {
    const trainingSets = [];

    const groupedProgramSets = groupBy(
      orderBy(programSets, ['setNumber', 'orderNumber']),
      'setNumber',
    );
    Object.values(groupedProgramSets).forEach(
      (programSetsGroup: IProgramSet[]) => {
        orderBy(programSetsGroup, ['setNumber', 'orderNumber']).forEach(
          (programSet) => {
            let trainingSetItemOrderNumber = 0;
            trainingSets.push({
              exerciseId: programSet.exerciseId,
              exercise: exercises.find((e) => e.id === programSet.exerciseId),
              setNumber: programSet.setNumber,
              orderNumber: programSet.orderNumber,
              temporaryId: generateTemporaryId(),
              trainingSetItems: orderBy(
                programSet.programSetItems,
                'orderNumber',
              ).flatMap((programSetItem) => {
                const trainingSetItems = [];

                for (let i = 0; i < programSetItem.sets; i++) {
                  trainingSetItems.push({
                    orderNumber: trainingSetItemOrderNumber,
                    rest: programSetItem.rest,
                    type: programSetItem.type,
                    status: TrainingSetItemStatus.notStarted,
                    temporaryId: generateTemporaryId(),
                    trainingSetItemOptions:
                      programSetItem.programSetItemOptions.map(
                        (setItemOption) => ({
                          optionId: setItemOption.optionId,
                          value: null,
                          value2: null,
                          programValue: setItemOption.value,
                          programValue2: setItemOption.value2,
                        }),
                      ),
                  });
                  trainingSetItemOrderNumber++;
                }

                return trainingSetItems;
              }),
            });
          },
        );
      },
    );

    return trainingSets;
  }

  function onProgramItemChange(e) {
    if (e?.target?.value) {
      getProgramItem(e.target.value.id);
      formik.setFieldValue('programItemId', e.target.value.id);
      formik.setFieldValue('programItem', e.target.value);
    } else {
      formik.setFieldValue('trainingSets', []);
      formik.setFieldValue('programItemId', null);
      formik.setFieldValue('programItem', null);
    }
  }

  const userProgramItems = useMemo(
    () =>
      orderBy(
        [...(programItems || []), ...(userAvailableProgramItems || [])].filter(
          Boolean,
        ),
        'name',
      ),
    [programItems, userAvailableProgramItems],
  );

  const exerciseResults = useExerciseResultsForSetAutofill(undefined);

  const { onAddEmptyTrainingSet, onAddTrainingSetFromSavedProgramSet } =
    useEditTraining(formik);

  return {
    t,
    formik,
    isLoading,
    isSubmitDisabled,
    defaultTrainingName,
    userProgramItems,
    onProgramItemChange,
    exerciseResults,
    onAddEmptyTrainingSet,
    onAddTrainingSetFromSavedProgramSet,
    showSavedTrainingModal,
    setShowSavedTrainingModal,
  };
}
